import { z } from 'zod';
import { PROPERTY_TYPES, UNIT_STATUSES } from '../enums.js';
import { nonNegativeMoneySchema } from '../money.js';
import { addressSchema, dateOnlySchema, mailingAddressSchema, optionalText, text, uuidSchema, } from './common.js';
export const propertyTypeSchema = z.enum(PROPERTY_TYPES);
export const unitStatusSchema = z.enum(UNIT_STATUSES);
export const createPropertySchema = z.object({
    name: text(200),
    propertyType: propertyTypeSchema,
    address: addressSchema,
    yearBuilt: z.coerce.number().int().min(1800).max(2100).nullish(),
    parcelNumber: optionalText(64),
    notes: optionalText(4000),
});
export const updatePropertySchema = createPropertySchema.partial();
export const createUnitSchema = z.object({
    propertyId: uuidSchema,
    /** "2B", "Upper", "1". Unique per property. */
    label: text(32),
    bedrooms: z.coerce.number().int().min(0).max(20),
    bathrooms: z.coerce.number().min(0).max(20),
    squareFeet: z.coerce.number().int().min(0).max(100000).nullish(),
    status: unitStatusSchema.default('AVAILABLE'),
    marketRent: nonNegativeMoneySchema,
    notes: optionalText(4000),
});
export const updateUnitSchema = createUnitSchema.omit({ propertyId: true }).partial();
/**
 * An owner's interest in a property over a period. Owners are scoped to
 * properties they *currently* hold, so `endDate` is what removes access — never
 * delete an ownership row, since the historical record is what justifies past
 * statements.
 */
export const createOwnershipSchema = z
    .object({
    propertyId: uuidSchema,
    ownerId: uuidSchema,
    /** Basis points, so a 33.33% split is exact. Must total 10000 per property. */
    basisPoints: z.coerce.number().int().min(1).max(10000).default(10000),
    startDate: dateOnlySchema,
    endDate: dateOnlySchema.nullish(),
})
    .refine((v) => !v.endDate || v.endDate >= v.startDate, {
    message: 'endDate must be on or after startDate.',
    path: ['endDate'],
});
export const createOwnerSchema = z.object({
    displayName: text(200),
    email: z.string().trim().toLowerCase().email().max(320),
    phone: optionalText(32),
    /** For an owner that's an LLC/trust/entity; `displayName` is the entity's own name. */
    contactPerson: optionalText(200),
    mailingAddress: mailingAddressSchema.nullish(),
    notes: optionalText(4000),
});
export const updateOwnerSchema = createOwnerSchema.partial();
export const createTenantSchema = z.object({
    firstName: text(100),
    lastName: text(100),
    email: z.string().trim().toLowerCase().email().max(320),
    phone: optionalText(32),
    dateOfBirth: dateOnlySchema.nullish(),
    emergencyContactName: optionalText(200),
    emergencyContactPhone: optionalText(32),
});
export const updateTenantSchema = createTenantSchema.partial();
/**
 * SSNs and screening notes go to `tenant_sensitive`, which owners have no policy
 * on at all (invariant 5). Keeping them in a separate schema makes it awkward to
 * accidentally accept one on the `tenants` endpoint.
 */
export const tenantSensitiveSchema = z.object({
    ssn: z
        .string()
        .trim()
        .regex(/^\d{3}-?\d{2}-?\d{4}$/, 'Enter a valid SSN.')
        .nullish(),
    driversLicense: optionalText(64),
    screeningNotes: optionalText(8000),
});
/**
 * Revoke or restore a tenant's ACH access — a bounced bank account after a
 * returned payment. A reason is required to revoke (staff will need it later
 * to know why); restoring needs none.
 */
export const setAchAccessSchema = z
    .object({
    revoked: z.boolean(),
    reason: optionalText(500),
})
    .refine((v) => !v.revoked || Boolean(v.reason), {
    message: 'A reason is required when revoking ACH access.',
    path: ['reason'],
});
export const ownerSensitiveSchema = z.object({
    taxId: z
        .string()
        .trim()
        .regex(/^\d{2}-?\d{7}$|^\d{3}-?\d{2}-?\d{4}$/, 'Enter a valid EIN or SSN.')
        .nullish(),
    /**
     * Tokenized reference only. Never a raw account or routing number — invariant
     * 5 and PCI/NACHA scope both depend on this never being stored here.
     */
    stripePayoutAccountRef: optionalText(255),
    notes: optionalText(8000),
});
/** Query shapes for the staff console list screens. */
/**
 * Free-text search plus the archived toggle.
 *
 * Shared by the properties, owners and tenants list endpoints — the three resources
 * that are searchable and archivable. Named for what it does rather than for the
 * first resource that used it, because a `propertyListQuerySchema` parsing a tenant
 * query reads as a copy-paste mistake every time someone new sees it.
 *
 * `includeArchived` arrives as a query STRING, so it is parsed from 'true'/'false'
 * rather than with z.coerce.boolean() — which returns true for any non-empty string
 * and would make `?includeArchived=false` mean the opposite of what it says.
 */
export const searchListQuerySchema = z.object({
    search: optionalText(200),
    includeArchived: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
});
export const unitListQuerySchema = z.object({
    propertyId: uuidSchema.optional(),
    status: unitStatusSchema.optional(),
    includeArchived: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
    /**
     * Excludes units with a live (ACTIVE/MONTH_TO_MONTH) lease. Deliberately
     * not the same thing as `status: 'AVAILABLE'` — `units.status` is a
     * cached hint that can drift (see modules/leases/service.ts), so this
     * checks the actual lease relation instead.
     */
    availableForLease: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
});
export const endOwnershipSchema = z.object({ endDate: dateOnlySchema });
/**
 * Correct an ownership row's owner in place — for a wrong-owner data-entry
 * mistake caught before any money has moved under it, never a real
 * transfer. See correctOwnershipOwner's own doc comment
 * (modules/properties/service.ts) for why this is a narrower, different
 * operation than end + re-grant.
 */
export const correctOwnershipOwnerSchema = z.object({ ownerId: uuidSchema });
//# sourceMappingURL=property.js.map